// Extra CMS slots for the 'community_guidelines' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const communityGuidelinesExtra: SlotDef[] = [
  // ─── Section headers (eyebrow / title / lede) ─────────────────────────
  {
    key: 'community_guidelines.lineage_eyebrow',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Lineage: eyebrow',
    default: 'The lineage',
  },
  {
    key: 'community_guidelines.lineage_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Lineage: title',
    default: 'Where this comes from.',
  },
  {
    key: 'community_guidelines.expectations_eyebrow',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Expectations: eyebrow',
    default: 'The expectation',
  },
  {
    key: 'community_guidelines.expectations_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Expectations: title',
    default: 'What we expect from everyone.',
  },
  {
    key: 'community_guidelines.expectations_lede',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Expectations: lede',
    default:
      'The promise runs both directions. Artists and hosts are held to the same standards. The language is just different.',
  },
  {
    key: 'community_guidelines.refusals_eyebrow',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Refusals: eyebrow',
    default: 'The refusal',
  },
  {
    key: 'community_guidelines.refusals_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Refusals: title',
    default: "What we won't tolerate.",
  },
  {
    key: 'community_guidelines.refusals_lede',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Refusals: lede',
    default:
      'Behaviours that break the network. Each one is grounds for a warning, a suspension, or, for the serious ones, removal.',
  },
  {
    key: 'community_guidelines.guardrails_eyebrow',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Mechanisms: eyebrow',
    default: 'The mechanism',
  },
  {
    key: 'community_guidelines.guardrails_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Mechanisms: title',
    default: 'How we hold the line.',
  },
  {
    key: 'community_guidelines.guardrails_lede',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Mechanisms: lede',
    default:
      'Standards on a page mean nothing without the machinery to enforce them. Five things keep WWAAVE honest in practice.',
  },

  // ─── Dispute-process callout card (sits under the mechanisms) ──────────
  {
    key: 'community_guidelines.dispute_callout_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Dispute callout: heading',
    default: 'The dispute process, in detail.',
  },
  {
    key: 'community_guidelines.dispute_callout_body',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Dispute callout: body',
    default:
      'The full five-step flow, with categories, timing, evidence handling and principles, lives on its own page.',
  },
  {
    key: 'community_guidelines.dispute_callout_link',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Dispute callout: link label',
    helpText: 'Visible text of the link. The link target (the dispute page) stays fixed.',
    default: 'Read the dispute process →',
  },

  // ─── Safety section header ────────────────────────────────────────────
  {
    key: 'community_guidelines.safety_eyebrow',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety: eyebrow',
    default: 'If you feel unsafe',
  },
  {
    key: 'community_guidelines.safety_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety: title',
    default: 'Your safety comes first.',
  },
  {
    key: 'community_guidelines.safety_lede',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety: lede',
    default:
      "Most stays are warm and uneventful. If one isn't, here's exactly what to do — in order.",
  },

  // ─── Safety card 1 — emergency services ───────────────────────────────
  // The emergency number renders bold, so it stays a separate slot; the copy
  // before and after it are the editable sentence halves around it.
  {
    key: 'community_guidelines.safety_emergency_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 1: heading',
    default: 'In immediate danger? Call emergency services first.',
  },
  {
    key: 'community_guidelines.safety_emergency_body_before',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 1: text before the number',
    helpText: 'The sentence continues into the bold emergency number.',
    default: 'If you or someone else is at risk right now, call',
  },
  {
    key: 'community_guidelines.safety_emergency_number',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 1: emergency number',
    helpText: 'Rendered bold, mid-sentence (e.g. the local emergency number).',
    default: '000',
  },
  {
    key: 'community_guidelines.safety_emergency_body_after',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 1: text after the number',
    helpText: 'The rest of the sentence, following the bold emergency number.',
    default:
      '(or your local emergency number) before anything else. Your safety matters more than any stay, exchange, or message on WWAAVE.',
  },

  // ─── Safety card 2 — reporting ────────────────────────────────────────
  // The email renders as a mailto link; its visible text is a slot while the
  // mailto target stays fixed. body_after opens with the period after the link.
  {
    key: 'community_guidelines.safety_report_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 2: heading',
    default: "Once you're safe, tell us.",
  },
  {
    key: 'community_guidelines.safety_report_body_before',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 2: text before the email',
    helpText: 'The sentence continues into the email link.',
    default: 'Use the Report option on any profile, message, or exchange, or email',
  },
  {
    key: 'community_guidelines.safety_report_email',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 2: email link label',
    helpText: 'Visible text of the email link. The mailto target stays fixed.',
    default: 'hello@wwaave.com',
  },
  {
    key: 'community_guidelines.safety_report_body_after',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 2: text after the email',
    helpText: 'The rest of the sentence, following the email link (begins with the full stop).',
    default:
      ". Tell us what happened in your own words — dates, names, links, whatever helps us act. You don't need to have proof to raise something.",
  },

  // ─── Safety card 3 — what happens next ────────────────────────────────
  {
    key: 'community_guidelines.safety_next_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 3: heading',
    default: 'What happens next.',
  },
  {
    key: 'community_guidelines.safety_next_body',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Safety card 3: body',
    default:
      "We act on a single credible report — you don't need a second voice to be taken seriously. Your report stays confidential; we never reveal who raised a concern. For serious safety reports, the account in question is suspended immediately, pending review.",
  },

  // ─── "Read on" next-steps tiles ───────────────────────────────────────
  // Block title + lede are editable; each tile's copy is a static per-item
  // slot. Tile routes stay hardcoded in the component.
  {
    key: 'community_guidelines.next_steps_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on: block title',
    default: 'Read on.',
  },
  {
    key: 'community_guidelines.next_steps_lede',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on: block lede',
    default:
      "The standards are the floor. The promise and the founder's story are why the floor sits where it does.",
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'community_guidelines.next_tile1_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on tile 1: title',
    helpText: 'Links to the WWAAVE Promise page.',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'community_guidelines.next_tile1_desc',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on tile 1: description',
    default: 'What WWAAVE is and isn’t, and the principles behind every decision.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'community_guidelines.next_tile2_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on tile 2: title',
    helpText: 'Links to the dispute-resolution page.',
    default: 'Dispute resolution',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'community_guidelines.next_tile2_desc',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on tile 2: description',
    default: 'The full process when an exchange goes wrong: categories, steps, principles.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'community_guidelines.next_tile3_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on tile 3: title',
    helpText: 'Links to the About page.',
    default: 'About WWAAVE',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'community_guidelines.next_tile3_desc',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Read on tile 3: description',
    default: 'Founded by a muso, not a tech bro. Where the platform came from.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
];
